/**
 * Lane C0-PROPS: the gates over the roads (C0_TOWN_LAYOUT §2 row 2 and the hub wall): the south paifang over the
 * wake spot (red, 128 px, three bays, the middle one higher) and the north gate (grey, 128 px, brick piers and a
 * gate roof: the tall arch on the back axis). Both drawn along x (their beam spans a north–south road); a gate along
 * y is the mirror (scenery.ts `alongAxis`), as the old painters do.
 */
import { box, brick, M, plaque, snap, wall } from '../buildings';
import type { Sprite } from '../scenery';
import { hipRoof, type Stage } from './common';
import { column, cornerTassels, done, K, paintedBeam, ROOF_TILES, stage, tassel, type RoofColour } from './parts';
import { lantern } from '../buildings';

/** A painted beam box from u0 to u1 along x, at y 0.35..0.65. */
function beam(s: Stage, u0: number, u1: number, z0: number, z1: number): void {
  box(s.px, s.P, [u0, 0.36, u1, 0.64], z0, z1, paintedBeam(z0, z1, 14), (u, v, l) => snap(K.maroon, l, u | 0, v | 0, 0));
}

/** The south paifang (牌坊): four columns on stone drums with clamp stones, three bays, three roofs, a plaque. */
export function paifang(L: number, colour: RoofColour): Sprite {
  const s = stage(L, 1, 134, 34);
  const tiles = ROOF_TILES[colour];
  const H = 96;
  const cols = [0.5, L * 0.3, L * 0.7, L - 0.5];
  // Clamp stones (抱鼓石) either side of each outer column.
  for (const x of cols) {
    box(s.px, s.P, [x - 0.3, 0.2, x + 0.3, 0.8], 0, 14, (u, v, l) => snap(M.stone, l + 0.05, u | 0, v | 0, 0.2), (u, v, l) => snap(M.stone, l, u | 0, v | 0, 0.2));
    column(s.px, s.P, x, 0.5, 0.15, 14, x === cols[1] || x === cols[2] ? H + 6 : H - 14, M.lacquer);
  }
  beam(s, 0.3, L - 0.3, H - 40, H - 32);
  beam(s, cols[1]! - 0.1, cols[2]! + 0.1, H - 10, H);
  beam(s, 0.3, cols[1]! + 0.1, H - 30, H - 22);
  beam(s, cols[2]! - 0.1, L - 0.3, H - 30, H - 22);
  plaque(s.px, s.P, 'left', 0.66, L / 2 - 1.1, L / 2 + 1.1, H - 30, H - 12, K.indigo);
  // Side roofs low, the middle roof high, each with horns and tassels.
  hipRoof(s, [0.0, -0.05, cols[1]! + 0.3, 1.05], H - 14, [0.5, 0.5, cols[1]!, 0.5], H - 2, tiles, { horns: 0.9 });
  hipRoof(s, [cols[2]! - 0.3, -0.05, L, 1.05], H - 14, [cols[2]!, 0.5, L - 0.5, 0.5], H - 2, tiles, { horns: 0.9 });
  hipRoof(s, [cols[1]! - 0.5, -0.15, cols[2]! + 0.5, 1.15], H + 6, [cols[1]! + 0.2, 0.5, cols[2]! - 0.2, 0.5], H + 28, tiles, { horns: 1.3 });
  cornerTassels(s.px, s.P, [cols[1]! - 0.5, -0.15, cols[2]! + 0.5, 1.15], H + 6);
  // Lanterns hung from the low beam in the middle bay.
  lantern(s.px, s.P(cols[1]! + 0.5, 0.5, H - 41), 6);
  lantern(s.px, s.P(cols[2]! - 0.5, 0.5, H - 41), 6);
  tassel(s.px, s.P(L / 2, 0.66, H - 32), 10);
  return done(s);
}

/** The north gate: brick piers with stone caps, a timber gate frame, a hip-and-gable roof spanning, a plaque. */
export function northGate(L: number, colour: RoofColour): Sprite {
  const s = stage(L, 1, 134, 34);
  const tiles = ROOF_TILES[colour];
  const H = 84;
  for (const [a, b] of [
    [-0.1, 1.1],
    [L - 1.1, L + 0.1],
  ] as const) {
    box(s.px, s.P, [a, -0.05, b, 1.05], 0, H, brick(H + 4), (u, v, l) => snap(M.stone, l, u | 0, v | 0, 0.2));
    box(s.px, s.P, [a - 0.05, -0.1, b + 0.05, 1.1], H - 6, H, (u, v, l) => snap(M.stone, l + 0.15, u | 0, v | 0, 0), (u, v, l) => snap(M.stone, l, u | 0, v | 0, 0));
    // A lamp niche.
    wall(s.px, s.P, 'left', 1.05, (a + b) / 2 - 0.2, (a + b) / 2 + 0.2, 44, 58, (u, v) => (v < 54 ? snap(M.red, 0.8, u | 0, v | 0, 0) : M.dark[1]!));
  }
  column(s.px, s.P, 1.3, 0.5, 0.14, 0, H, M.lacquer);
  column(s.px, s.P, L - 1.3, 0.5, 0.14, 0, H, M.lacquer);
  beam(s, 1.1, L - 1.1, H - 12, H);
  plaque(s.px, s.P, 'left', 0.66, L / 2 - 1.2, L / 2 + 1.2, H - 28, H - 13, M.dark);
  hipRoof(s, [-0.4, -0.35, L + 0.4, 1.35], H, [1.2, 0.5, L - 1.2, 0.5], 124, tiles, { horns: 1.3, tassels: true });
  return done(s);
}
